import PropTypes from "prop-types";
import { useCallback, useEffect, useRef } from "react";
import useSuppressChat from "../../../hooks/useSuppressChat";
import s from "./Drawer.module.css";
import { CloseIcon } from "./Icons.jsx";

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const CLOSE_THRESHOLD = 80;

export default function Drawer({ open, onClose, title, ariaLabel, children }) {
  useSuppressChat(open);
  const drawerRef = useRef(null);
  const previousFocus = useRef(null);
  const dragState = useRef(null);

  useEffect(() => {
    if (open) {
      previousFocus.current = document.activeElement;
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open || !drawerRef.current) return;
    const first = drawerRef.current.querySelector(FOCUSABLE);
    if (first) first.focus();
    return () => {
      if (previousFocus.current && previousFocus.current.focus) {
        previousFocus.current.focus();
      }
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll(FOCUSABLE);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  // --- Swipe-to-close logic ---

  const applyTranslate = useCallback((dy) => {
    const el = drawerRef.current;
    if (!el) return;
    const clamped = Math.max(0, dy);
    el.style.transform = `translateY(${clamped}px)`;
    el.style.transition = "none";
  }, []);

  const resetTranslate = useCallback((animate) => {
    const el = drawerRef.current;
    if (!el) return;
    if (animate) {
      el.style.transition = "transform 0.2s ease-out";
    }
    el.style.transform = "";
    // Clean up after transition
    if (animate) {
      const cleanup = () => {
        el.style.transition = "";
        el.removeEventListener("transitionend", cleanup);
      };
      el.addEventListener("transitionend", cleanup);
    }
  }, []);

  const handleDragStart = useCallback((e) => {
    // Only start drag if the drawer is scrolled to the top
    const el = drawerRef.current;
    if (el && el.scrollTop > 0) return;

    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    dragState.current = { startY: clientY, currentY: clientY };

    // Prevent scroll while dragging
    if (el) el.style.overflowY = "hidden";
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!dragState.current) return;
    const dy = dragState.current.currentY - dragState.current.startY;
    dragState.current = null;

    const el = drawerRef.current;
    if (el) el.style.overflowY = "";

    if (dy > CLOSE_THRESHOLD) {
      // Animate off-screen then close
      if (el) {
        el.style.transition = "transform 0.2s ease-in";
        el.style.transform = "translateY(100%)";
        const finish = () => {
          el.style.transition = "";
          el.style.transform = "";
          el.removeEventListener("transitionend", finish);
          onClose();
        };
        el.addEventListener("transitionend", finish);
      } else {
        onClose();
      }
    } else {
      resetTranslate(true);
    }
  }, [onClose, resetTranslate]);

  // Attach pointermove/pointerup on document so drag continues outside the handle
  useEffect(() => {
    if (!open) return;

    const onMove = (e) => {
      if (!dragState.current) return;
      const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
      dragState.current.currentY = clientY;
      const dy = clientY - dragState.current.startY;
      applyTranslate(dy);
    };

    const onEnd = () => {
      if (!dragState.current) return;
      handleDragEnd();
    };

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onEnd);
    document.addEventListener("pointercancel", onEnd);
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);

    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onEnd);
      document.removeEventListener("pointercancel", onEnd);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [open, applyTranslate, handleDragEnd]);

  if (!open) return null;

  return (
    <div
      className={s.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={drawerRef}
        className={s.drawer}
        role="dialog"
        aria-label={ariaLabel || title}
        aria-modal="true"
      >
        <div
          className={s.dragHandle}
          onPointerDown={handleDragStart}
          onTouchStart={handleDragStart}
        />
        <div className={s.header}>
          {typeof title === "string" ? (
            <p className={s.title}>{title}</p>
          ) : (
            title
          )}
          <button className={s.closeBtn} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

Drawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
};
